const BASE_URL = process.env.REACT_APP_API_URL || "";

// Absolute base for full-page redirects (e.g. OAuth flows) that can't go
// through fetch(). Empty string in dev falls back to same-origin (CRA proxy).
export const apiBaseUrl = BASE_URL;

const TOKEN_KEY = "clubpm_auth_token";

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function authHeaders() {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

// Optional viewport origin set by a UI action right before it triggers a
// reward-granting request. Lets reward particles spawn at the action site
// (e.g. the card the user just dropped) instead of screen center.
// Auto-cleared after a short TTL so stale origins don't leak.
let _nextRewardOrigin = null;
let _nextRewardOriginTimer = null;

export function setNextRewardOrigin(x, y, ttlMs = 1500) {
  if (typeof x !== "number" || typeof y !== "number") return;
  _nextRewardOrigin = { x, y };
  if (_nextRewardOriginTimer) clearTimeout(_nextRewardOriginTimer);
  _nextRewardOriginTimer = setTimeout(() => { _nextRewardOrigin = null; }, ttlMs);
}

function dispatchRewardSignals(payload) {
  if (!payload || typeof payload !== "object") return;
  // Achievement auto-unlocks are surfaced by the task PATCH endpoint when the
  // server granted their XP/DB silently. Roll those into the visible delta so
  // RewardFlux fires, then emit a separate event so the modal listener can pop.
  const achievementUnlocks = Array.isArray(payload.achievementUnlocks) ? payload.achievementUnlocks : [];
  const achXp = achievementUnlocks.reduce((s, a) => s + Number(a?.xpReward ?? 0), 0);
  const achDb = achievementUnlocks.reduce((s, a) => s + Number(a?.doubloonReward ?? 0), 0);
  const xpDelta = Number(payload.xpDelta ?? 0) + achXp;
  const doubloonsDelta = Number(payload.doubloonsDelta ?? 0) + achDb;
  const rankAfter = payload.rankAfter ?? null;
  const rankBefore = payload.rankBefore ?? null;

  // Queued reward (admin-gated task completion) — no delta, but still surface
  // a confirmation toast so the user knows the action was registered.
  if (payload.queued === true && payload.taskTitle) {
    window.dispatchEvent(new CustomEvent("clubpm:reward-queued", {
      detail: { taskTitle: payload.taskTitle },
    }));
  }

  for (const unlock of achievementUnlocks) {
    if (!unlock?.name) continue;
    window.dispatchEvent(new CustomEvent("clubpm:achievement-unlocked", { detail: unlock }));
  }

  if (xpDelta || doubloonsDelta) {
    const origin = _nextRewardOrigin;
    _nextRewardOrigin = null;
    if (_nextRewardOriginTimer) { clearTimeout(_nextRewardOriginTimer); _nextRewardOriginTimer = null; }
    window.dispatchEvent(new CustomEvent("clubpm:reward-granted", {
      detail: {
        xpDelta,
        doubloonsDelta,
        newXp: payload.newXp ?? null,
        newDoubloons: payload.newDoubloons ?? null,
        origin,
      },
    }));
  }
  if (rankAfter && rankBefore && rankAfter !== rankBefore) {
    window.dispatchEvent(new CustomEvent("clubpm:member-updated"));
  } else if (xpDelta || doubloonsDelta) {
    window.dispatchEvent(new CustomEvent("clubpm:member-updated"));
  }

  // Cosmetic unlock popup — fires whenever any response grants a new cosmetic.
  // Supported fields: unlockedCosmetic (single) or unlockedCosmetics (array).
  const singles = payload.unlockedCosmetic
    ? [payload.unlockedCosmetic]
    : Array.isArray(payload.unlockedCosmetics)
      ? payload.unlockedCosmetics
      : [];
  for (const cosmetic of singles) {
    if (!cosmetic?.name) continue;
    window.dispatchEvent(new CustomEvent("clubpm:cosmetic-unlocked", { detail: cosmetic }));
  }

  // Quest / challenge progress milestones — fires a toast at 25/50/75/100% bands.
  // progressMilestones: [{ challengeId, challengeName?, pct }]
  const milestones = Array.isArray(payload.progressMilestones) ? payload.progressMilestones : [];
  for (const m of milestones) {
    if (typeof m?.pct !== "number") continue;
    window.dispatchEvent(new CustomEvent("clubpm:challenge-progress", { detail: m }));
  }
}

async function handleResponse(response, method = "GET", requestPath = "") {
  if (response.status === 401) {
    // Throw without redirecting. AppShell already redirects via React Router's
    // <Navigate> when member is null — a hard window.location redirect would
    // hit a GitHub Pages 404 for any /clubpm/* route, and would also fire on
    // 401s from non-auth endpoints (e.g. Slack integration) even when the user's
    // own token is valid.
    throw new ApiError(401, "Not authenticated");
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new ApiError(
      response.status,
      body.error ?? "Request failed"
    );
  }

  const body = await response.json();
  dispatchRewardSignals(body);
  dispatchApiSuccess(method, requestPath);
  return body;
}

/**
 * Lets a walkthrough step advance on a real successful write rather than on a
 * click that may or may not have done anything. One dispatch here covers every
 * endpoint, because every call in this client funnels through handleResponse.
 *
 * The path is normalised first: step files declare "/api/tasks", but the client
 * calls "/api/tasks?projectId=…" often enough that an un-stripped query string
 * would leave those steps waiting on a request that already succeeded.
 */
export function dispatchApiSuccess(method, requestPath) {
  const path = String(requestPath ?? "").split(/[?#]/)[0];
  // The tour's own progress pings run through this client too. They can never
  // match a declared step path today, but a tour endpoint advancing a tour step
  // is a feedback loop, and the cheapest place to rule it out is here.
  if (path.includes("/tour-progress") || path.includes("/tour-breakage")) return;
  window.dispatchEvent(new CustomEvent("clubpm:api-success", {
    detail: { method, path },
  }));
}

export async function get(path) {
  const response = await fetch(`${BASE_URL}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json", ...authHeaders() },
  });
  return handleResponse(response, "GET", path);
}

export async function post(path, data) {
  // FormData goes through untouched and WITHOUT a Content-Type header — the
  // browser has to set it so the multipart boundary is included. Same idiom as
  // uploadVaultFile / uploadBlogImage, just without the progress plumbing.
  const isForm = typeof FormData !== "undefined" && data instanceof FormData;
  const response = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      ...(isForm ? {} : { "Content-Type": "application/json" }),
      Accept: "application/json",
      ...authHeaders(),
    },
    body: isForm ? data : JSON.stringify(data),
  });
  return handleResponse(response, "POST", path);
}

export async function put(path, data) {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify(data),
  });
  return handleResponse(response, "PUT", path);
}

export async function patch(path, data) {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify(data),
  });
  return handleResponse(response, "PATCH", path);
}

export async function del(path, data) {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: "DELETE",
    credentials: "include",
    headers: {
      ...(data === undefined ? {} : { "Content-Type": "application/json" }),
      Accept: "application/json",
      ...authHeaders(),
    },
    ...(data === undefined ? {} : { body: JSON.stringify(data) }),
  });

  if (response.status === 401) {
    throw new ApiError(401, "Not authenticated");
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new ApiError(
      response.status,
      body.error ?? "Delete failed"
    );
  }

  dispatchApiSuccess("DELETE", path);

  // Several DELETE endpoints answer 200 with the recomputed row (detaching a
  // blocker returns the task with its new status). Returning undefined for all
  // of them made every such caller read a property off undefined and roll its
  // optimistic update back as if the server had refused.
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (!text) return undefined;
  try { return JSON.parse(text); } catch { return undefined; }
}

// ── Engagement: streak / inventory / shop consumables ─────────

// Tiny module-scope cache so multiple consumers (StreakBadge + dashboard tile)
// don't trigger duplicate network calls within the freshness window.
let _streakCache = { memberId: null, fetchedAt: 0, data: null, inflight: null };
const STREAK_CACHE_TTL_MS = 5_000;

export async function getStreak(memberId, { force = false } = {}) {
  const now = Date.now();
  if (
    !force &&
    _streakCache.memberId === memberId &&
    _streakCache.data &&
    now - _streakCache.fetchedAt < STREAK_CACHE_TTL_MS
  ) {
    return _streakCache.data;
  }
  if (_streakCache.inflight && _streakCache.memberId === memberId) {
    return _streakCache.inflight;
  }
  _streakCache.memberId = memberId;
  _streakCache.inflight = get(`/api/members/${memberId}/streak`).then((data) => {
    _streakCache.data = data;
    _streakCache.fetchedAt = Date.now();
    _streakCache.inflight = null;
    return data;
  }).catch((err) => {
    _streakCache.inflight = null;
    throw err;
  });
  return _streakCache.inflight;
}

export function invalidateStreakCache() {
  _streakCache = { memberId: null, fetchedAt: 0, data: null, inflight: null };
}

export async function getActivity(memberId, days = 30) {
  return get(`/api/members/${memberId}/activity?days=${days}`);
}

export async function pollCelebration() {
  return get(`/api/members/me/celebration`);
}

export async function getInventory() {
  return get(`/api/inventory`);
}

export async function useInventoryItem(itemKey) {
  return post(`/api/inventory/use`, { itemKey });
}

export async function getConsumables() {
  return get(`/api/shop/consumables`);
}

export async function purchaseConsumable(itemKey) {
  return post(`/api/shop/purchase-consumable`, { itemKey });
}

// ── Progress snapshot ─────────────────────────────────────────
//
// Per-user snapshot of the milestone-progress values they last saw, so we can
// animate bars from the old value to the new on project mount. Saves are
// fire-and-forget; failures are logged but never block the UI.

export async function getProgressSnapshot() {
  try { return await get("/api/members/me/progress-snapshot"); }
  catch { return null; }
}

export function saveProgressSnapshot(payload) {
  return put("/api/members/me/progress-snapshot", payload).catch(err => {
    console.warn("[clubpm] saveProgressSnapshot failed", err);
  });
}

// ── Member iCal feed ────────────────────────────────────────────
//
// The URL is write-only from here: the server stores it encrypted and returns
// only the host, so nothing ever repopulates the input after a reload.

export const getIcsFeed    = ()           => get('/api/members/me/ics-feed');
export const saveIcsFeed   = (url, label) => put('/api/members/me/ics-feed', { url, label });
export const removeIcsFeed = ()           => del('/api/members/me/ics-feed');

// ── Task archiving ──────────────────────────────────────────────

export const archiveTask       = (id) => post(`/api/tasks/${id}/archive`, {});
export const unarchiveTask     = (id) => post(`/api/tasks/${id}/unarchive`, {});
export const bulkArchive       = (ids, archived) => post(`/api/tasks/bulk-archive`, { ids, archived });
export const previewBulkMove   = (ids, projectId) => post(`/api/tasks/bulk-move/preview`, { ids, projectId });
export const bulkMoveTasks     = (ids, projectId, opts = {}) => post(`/api/tasks/bulk-move`, { ids, projectId, ...opts });
export const bulkDuplicateTasks = (ids) => post(`/api/tasks/bulk-duplicate`, { ids });
export const getArchivedTasks  = (projectId) => get(`/api/projects/${projectId}/tasks?archived=1`);

// Slack backlinks (filtered by the viewer's conversation access).
export const getSlackLinks = (entityType, entityId) => get(`/api/slack-links?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`);
export const deleteSlackLink = (id) => del(`/api/slack-links/${encodeURIComponent(id)}`);

// ── Blockers ─────────────────────────────────────────────────

export const getProjectBlockers = (projectId) => get(`/api/projects/${projectId}/blockers`);
export const createBlocker      = (projectId, data) => post(`/api/projects/${projectId}/blockers`, data);
export const updateBlocker      = (id, data) => patch(`/api/blockers/${id}`, data);

// ── Vault (Constellation Vault CAD change management) ──────────

export const getVault             = (projectId, { rollup } = {}) => get(`/api/projects/${projectId}/vault${rollup ? "?rollup=1" : ""}`);
export const getVaultItem         = (id) => get(`/api/vault/items/${id}`);
export const patchVaultItem       = (id, body) => patch(`/api/vault/items/${id}`, body);
export const deleteVaultItem      = (id) => del(`/api/vault/items/${id}`);
export const checkoutVaultItem    = (id, body) => post(`/api/vault/items/${id}/checkout`, body);
export const releaseVaultCheckout = (id) => del(`/api/vault/items/${id}/checkout`);
export const promoteVaultItem     = (id) => post(`/api/vault/items/${id}/promote`, {});
export const getVaultItemHistory  = (id) => get(`/api/vault/items/${id}/history`);
export const patchVaultSettings   = (projectId, body) => patch(`/api/projects/${projectId}/vault/settings`, body);
// Admin only: flag a project member as a lead (admin task powers in that project).
export const setProjectLead = (projectId, memberId, isLead, leadTitle = null) => put(`/api/projects/${projectId}/members/${memberId}/lead`, { isLead, leadTitle });
export const getVaultRepository = (projectId) => get(`/api/projects/${projectId}/vault/repository`);
export const getVaultRepositoryHealth = (projectId) => get(`/api/projects/${projectId}/vault/repository/health`);
export const getVaultCommits = (projectId) => get(`/api/projects/${projectId}/vault/repository/commits`);
export const setVaultRepository = (projectId, body) => put(`/api/projects/${projectId}/vault/repository`, body);
export const verifyVaultRepository = (projectId) => post(`/api/projects/${projectId}/vault/repository/verify`, {});
export const setVaultGithubWrites = (projectId, writeEnabled) => patch(`/api/projects/${projectId}/vault/repository`, { writeEnabled });
export const getVaultUploadJob = (id) => get(`/api/vault/upload-jobs/${id}`);
export const retryVaultUploadJob = (id, body = {}) => post(`/api/vault/upload-jobs/${id}/retry`, body);
export const compareVaultVersions = (itemId, beforeId, afterId) => get(`/api/vault/items/${itemId}/compare?before=${encodeURIComponent(beforeId)}&after=${encodeURIComponent(afterId)}`);
export const vaultDownloadUrl     = (versionId) => `${BASE_URL}/api/vault/versions/${versionId}/download`;

// Multipart vault upload (new item or new version) with a live progress
// callback. get/post/patch/del only send JSON, and fetch() has no upload
// progress event, so this goes straight through XHR (same FormData idiom as
// uploadBlogImage, plus xhr.upload.onprogress).
export function uploadVaultFile(path, file, fields = {}, onProgress, headers = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE_URL}${path}`);
    xhr.withCredentials = true;
    // Bearer token for cross-origin users whose session cookie is blocked —
    // same auth the JSON helpers and uploadBlogImage send.
    Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      let body = null; try { body = JSON.parse(xhr.responseText); } catch {}
      if (xhr.status >= 200 && xhr.status < 300) {
        // Uploads bypass handleResponse, so without this a walkthrough step that
        // advances on POST /api/vault/items/:id/versions would wait forever.
        dispatchApiSuccess("POST", path);
        resolve(body);
      }
      else reject(Object.assign(new Error(body?.error || `Upload failed (${xhr.status})`), { status: xhr.status, body }));
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(fields).forEach(([k, v]) => v != null && fd.append(k, v));
    xhr.send(fd);   // no Content-Type header — FormData sets the multipart boundary
  });
}

// ── Change requests (Constellation Vault CAD change management) ──

export const listCrs   = (projectId, status) =>
  get(`/api/projects/${projectId}/change-requests${status ? `?status=${encodeURIComponent(status)}` : ""}`);
export const createCr  = (projectId, body) => post(`/api/projects/${projectId}/change-requests`, body);
export const getCr     = (id) => get(`/api/change-requests/${id}`);
export const patchCr   = (id, body) => patch(`/api/change-requests/${id}`, body);
export const cancelCr  = (id) => post(`/api/change-requests/${id}/cancel`, {});
export const approveCr = (id, body = {}) => post(`/api/change-requests/${id}/approve`, body);
export const rejectCr  = (id, body = {}) => post(`/api/change-requests/${id}/reject`, body);
export const getCrPendingCount = () => get(`/api/change-requests/pending/count`);
export const getCrReviewStatus = (id) => get(`/api/change-requests/${id}/review-status`);
export const linkCrPr = (id, body) => post(`/api/change-requests/${id}/pr`, body);
export const syncCrPr = (id) => post(`/api/change-requests/${id}/pr/sync`, {});
export const signoffCr = (id) => post(`/api/change-requests/${id}/signoff`, {});
export const revokeCrSignoff = (id) => del(`/api/change-requests/${id}/signoff`);
export const getVaultReviewerRules = (projectId) => get(`/api/projects/${projectId}/vault/reviewer-rules`);
export const saveVaultReviewerRules = (projectId, body) => put(`/api/projects/${projectId}/vault/reviewer-rules`, body);

// Vault releases (Phase 7): build readiness, drawing requirements, manifests and packages
export const getCrReadiness = (id) => get(`/api/change-requests/${id}/readiness`);
export const getCrRelease = (id) => get(`/api/change-requests/${id}/release`);
export const listVaultReleases = (projectId) => get(`/api/projects/${projectId}/vault/releases`);
export const getVaultDrawingRequirements = (projectId) => get(`/api/projects/${projectId}/vault/drawing-requirements`);
export const saveVaultDrawingRequirements = (projectId, requirements) => put(`/api/projects/${projectId}/vault/drawing-requirements`, { requirements });
export const setVaultDrawingFor = (itemId, drawingForId) => put(`/api/vault/items/${itemId}/drawing-for`, { drawingForId });
export const getVaultRelease = (releaseId) => get(`/api/vault-releases/${releaseId}`);
export const getVaultReleasePackageUrl = (releaseId) => get(`/api/vault-releases/${releaseId}/package-url`);
export const buildVaultReleasePackage = (releaseId) => post(`/api/vault-releases/${releaseId}/package`, {});
export const verifyVaultRelease = (releaseId) => post(`/api/vault-releases/${releaseId}/verify`, {});
// Phase 9 geometry diff. POST returns the cached row (DONE) or a queued one
// (PENDING/RUNNING) to poll with GET until DONE or FAILED.
export const requestVaultGeometryDiff = (body) => post(`/api/vault/geometry-diffs`, body);
export const getVaultGeometryDiff = (diffId) => get(`/api/vault/geometry-diffs/${diffId}`);
export const getVaultGeometryCapabilities = () => get(`/api/vault/geometry/capabilities`);
// Binary STL of a STEP side's tessellation — what the diff actually measured.
export const vaultGeometryMeshUrl = (diffId, side) => `${BASE_URL}/api/vault/geometry-diffs/${diffId}/mesh/${side}`;
// The manifest is small; fetch its exact bytes (never re-serialized JSON) for saving.
export async function getVaultReleaseManifestText(releaseId) {
  const response = await fetch(`${BASE_URL}/api/vault-releases/${releaseId}/manifest`, { credentials: "include", headers: authHeaders() });
  if (!response.ok) throw new Error(`Manifest unavailable (${response.status})`);
  return response.text();
}

// Vault BOM / where-used (Pack C)
export const addVaultBomLink    = (itemId, body) => post(`/api/vault/items/${itemId}/bom`, body);
export const updateVaultBomLink = (itemId, childItemId, body) => patch(`/api/vault/items/${itemId}/bom/${childItemId}`, body);
export const removeVaultBomLink = (itemId, childItemId) => del(`/api/vault/items/${itemId}/bom/${childItemId}`);

// Short-lived signed URL for <a href>/<img> access to vault binaries (those
// elements can't carry the Bearer header). Returns { url, expiresAt }.
export const getVaultVersionDownloadUrl = (versionId) => get(`/api/vault/versions/${versionId}/download-url`);

// Vault AI copilot (Pack B)
// ── Vault search, saved views, subscriptions (Phase 8) ────────
// Search params: q, projectId, kinds[], released, checkedOut, crStatus,
// fileExts[], authorId ("me"), watching, since, until, sort, limit, offset.
export function searchVault(params = {}) {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0)) continue;
    qs.set(key, Array.isArray(value) ? value.join(",") : String(value));
  }
  return get(`/api/vault/search?${qs.toString()}`);
}
export const listVaultSavedViews  = (projectId) => get(`/api/vault/saved-views${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}`);
export const createVaultSavedView = (body) => post(`/api/vault/saved-views`, body);
export const updateVaultSavedView = (id, body) => patch(`/api/vault/saved-views/${id}`, body);
export const deleteVaultSavedView = (id) => del(`/api/vault/saved-views/${id}`);
export const getVaultSubscription = (itemId) => get(`/api/vault/items/${itemId}/subscription`);
export const setVaultSubscription = (itemId, body) => put(`/api/vault/items/${itemId}/subscription`, body);
export const unwatchVaultItem     = (itemId) => del(`/api/vault/items/${itemId}/subscription`);
export const listVaultSubscriptions = (projectId) => get(`/api/vault/subscriptions${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}`);
export const rebuildVaultSearchIndex = (projectId) => post(`/api/projects/${projectId}/vault/search/rebuild`, {});

export const askVault             = (projectId, question) => post(`/api/projects/${projectId}/vault/ask`, { question });
export const checkVaultDuplicates = (projectId, body) => post(`/api/projects/${projectId}/vault/check-duplicates`, body);
export const aiCrReleaseNotes     = (id) => post(`/api/change-requests/${id}/ai-release-notes`, {});
export const aiCrImpact           = (id) => post(`/api/change-requests/${id}/ai-impact`, {});

// ── AI action plan ─────────────────────────────────────────────

export const suggestActions = (projectId, goal) => post(`/api/projects/${projectId}/ai-suggest-actions`, { goal });
export const executePlan    = (projectId, actions) => post(`/api/projects/${projectId}/ai-execute-plan`, { actions });

// Clipboard planning lane: build the prompt locally, paste the reply back.
// Neither call spends AI quota — see backend/src/services/aiActionService.ts.
export const getAiPlanPrompt = (projectId, goal) => post(`/api/projects/${projectId}/ai-plan-prompt`, { goal });
export const importAiPlan    = (projectId, raw)  => post(`/api/projects/${projectId}/ai-plan-import`, { raw });

// ── Challenges / Achievements ─────────────────────────────────

export const getActiveChallenges  = () => get('/api/challenges/active');
export const claimChallenge       = (id) => post(`/api/challenges/${id}/claim`, {});
export const getAchievements      = () => get('/api/challenges/achievements');
export const getChallengeHistory  = (days = 30) => get(`/api/challenges/history?days=${days}`);
export const getChallengesCatalog = () => get('/api/challenges/catalog');

// ── Blog editor ───────────────────────────────────────────────

export const listBlogPosts   = (params = '') => get(`/api/blog/posts${params}`);
export const getBlogPost      = (id) => get(`/api/blog/posts/${id}`);
export const createBlogPost   = (data) => post('/api/blog/posts', data);
export const generateBlogPost = (data) => post('/api/blog/posts/generate', data);
// Generates the article doc without creating a post — for the in-editor AI panel.
export const generateBlogDoc  = (data) => post('/api/blog/posts/generate-doc', data);
// "Plan with Claude" clipboard lane: build prompt → validate pasted reply → create draft.
export const getBlogPlanPrompt      = (data)        => post('/api/blog/posts/plan-prompt', data);
export const importBlogPlan         = (raw)         => post('/api/blog/posts/plan-import', { raw });
export const createBlogPostFromPlan = (plan, title) => post('/api/blog/posts/from-plan', { plan, title });
export const updateBlogPost   = (id, data) => patch(`/api/blog/posts/${id}`, data);
export const previewBlogPost = (id, contentJson) => post(`/api/blog/posts/${id}/preview`, { contentJson });
export const deleteBlogPost   = (id) => del(`/api/blog/posts/${id}`);
export const publishBlogPost  = (id) => post(`/api/blog/posts/${id}/publish`, {});
export const scheduleBlogPost = (id, scheduledAt) => post(`/api/blog/posts/${id}/schedule`, { scheduledAt });
export const unpublishBlogPost = (id) => post(`/api/blog/posts/${id}/unpublish`, {});
export const archiveBlogPost  = (id) => post(`/api/blog/posts/${id}/archive`, {});
export const listBlogRevisions = (id) => get(`/api/blog/posts/${id}/revisions`);
export const rollbackBlogRevision = (id, revId) => post(`/api/blog/posts/${id}/revisions/${revId}/rollback`, {});
export const renameBlogRevision = (id, revId, name) => patch(`/api/blog/posts/${id}/revisions/${revId}`, { name });
export const listBlogTags     = () => get('/api/blog/tags');
export const createBlogTag    = (name) => post('/api/blog/tags', { name });
export const listBlogCategories = () => get('/api/blog/categories');
export const createBlogCategory = (name) => post('/api/blog/categories', { name });
export const setBlogTaxonomy  = (id, tagIds, categoryIds) => put(`/api/blog/posts/${id}/taxonomy`, { tagIds, categoryIds });
export const listBlogSnippets = () => get('/api/blog/snippets');
export const createBlogSnippet = (name, contentJson) => post('/api/blog/snippets', { name, contentJson });
export const updateBlogSnippet = (id, data) => patch(`/api/blog/snippets/${id}`, data);
export const deleteBlogSnippet = (id) => del(`/api/blog/snippets/${id}`);
export const addBlogAuthor    = (id, memberId) => post(`/api/blog/posts/${id}/authors`, { memberId });
export const removeBlogAuthor = (id, memberId) => del(`/api/blog/posts/${id}/authors/${memberId}`);
export const listBlogAnnotations = (id) => get(`/api/blog/posts/${id}/annotations`);
export const addBlogAnnotation = (id, body, parentId) => post(`/api/blog/posts/${id}/annotations`, { body, parentId });

// ── Blog / press-kit review threads ──────────────────────────
export const listBlogThreads        = (docType, docId) => get(`/api/blog/docs/${docType}/${docId}/threads`);
export const createBlogThread       = (docType, docId, body) => post(`/api/blog/docs/${docType}/${docId}/threads`, body);
export const setBlogThreadStatus    = (threadId, status) => patch(`/api/blog/threads/${threadId}`, { status });
export const addBlogThreadComment   = (threadId, body) => post(`/api/blog/threads/${threadId}/comments`, { body });
export const editBlogThreadComment  = (threadId, commentId, body) => patch(`/api/blog/threads/${threadId}/comments/${commentId}`, { body });
export const deleteBlogThreadComment = (threadId, commentId) => del(`/api/blog/threads/${threadId}/comments/${commentId}`);
// Base64-encoded Yjs relative positions. Comment anchors live here rather than
// in the document — see src/components/clubpm/blog/threadAnchors.js.
export const setBlogThreadAnchor    = (threadId, anchorStart, anchorEnd) => patch(`/api/blog/threads/${threadId}/anchor`, { anchorStart, anchorEnd });

// ── Blog / press-kit AI ──────────────────────────────────────
export const blogAiAsk      = (docType, docId, question) => post('/api/blog/ai/ask', { docType, docId, question });
export const blogAiEdit     = (docType, docId, body) => post('/api/blog/ai/edit', { docType, docId, ...body });
export const blogAiComplete = (docType, docId, before) => post('/api/blog/ai/complete', { docType, docId, before });

// ws(s):// base for the embedded Hocuspocus collab server (backend/src/collab/blogCollab.ts,
// mounted at /collab/blog). Mirrors BASE_URL's origin, swapping the http(s) scheme for ws(s);
// falls back to the current page's origin so the CRA dev proxy forwards the upgrade request.
export function getBlogCollabWsUrl() {
  const origin = BASE_URL || window.location.origin;
  return `${origin.replace(/^http/, 'ws')}/collab/blog`;
}

// ── Press Kit ────────────────────────────────────────────────
export const getPressKit            = (projectId)         => get(`/api/projects/${projectId}/press-kit`);
export const generatePressKit       = (projectId, config) => post(`/api/projects/${projectId}/press-kit/generate`, config);
export const updatePressKitConfig   = (projectId, config) => patch(`/api/projects/${projectId}/press-kit`, config);
export const publishPressKit        = (projectId)         => post(`/api/projects/${projectId}/press-kit/publish`, {});
export const getPressKitRevisions   = (projectId)         => get(`/api/projects/${projectId}/press-kit/revisions`);
export const restorePressKitRevision = (projectId, revId) => post(`/api/projects/${projectId}/press-kit/revisions/${revId}/restore`, {});
// REST fallback save for the press-kit body (used when the collab WS is down).
export const updatePressKitContent   = (projectId, contentJson) => patch(`/api/projects/${projectId}/press-kit/content`, { contentJson });
export const deletePressKit = (projectId) => del(`/api/projects/${projectId}/press-kit`);

// Authenticated file download for press-kit exports (a plain <a href> omits the
// Bearer header). Mirrors downloadMeetingPollIcs.
export async function downloadPressKitExport(projectId, format, projectName = 'press-kit') {
  const ext = ({ pdf: 'pdf', docx: 'docx', md: 'md', html: 'html' })[format] || 'txt';
  const response = await fetch(`${BASE_URL}/api/projects/${projectId}/press-kit/export?format=${format}`, {
    credentials: 'include',
    headers: { ...authHeaders() },
  });
  if (!response.ok) {
    // Surface the server's reason — a 503 tells the user PDF is unavailable on
    // this server but the other formats still work, which 'Export failed' hides.
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, body.error ?? 'Export failed');
  }
  const blob = await response.blob();
  const safe = String(projectName).replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'press-kit';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${safe}.${ext}`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ws(s):// base for the press-kit Hocuspocus namespace (backend/src/collab/pressKitCollab.ts).
export function getPressKitCollabWsUrl() {
  const origin = BASE_URL || window.location.origin;
  return `${origin.replace(/^http/, 'ws')}/collab/presskit`;
}

// ── Courses (Outreach → Courses) ─────────────────────────────
export const listCourses    = ()        => get('/api/outreach/courses');
export const getCourse      = (id)      => get(`/api/outreach/courses/${id}`);
export const createCourse   = (data)    => post('/api/outreach/courses', data);
export const updateCourse   = (id, data) => patch(`/api/outreach/courses/${id}`, data);
export const publishCourse  = (id)      => post(`/api/outreach/courses/${id}/publish`, {});
// Back to DRAFT, keeping enrollments and progress — see courseService.unpublishCourse.
export const unpublishCourse = (id)     => post(`/api/outreach/courses/${id}/unpublish`, {});
export const archiveCourse  = (id)      => post(`/api/outreach/courses/${id}/archive`, {});
export const deleteCourse   = (id)      => del(`/api/outreach/courses/${id}`);

// ── AI course generation ─────────────────────────────────────
// Two stages behind a CourseGenJob row: startCourseGen drafts an outline, the
// author edits it (saveCourseGenOutline), then runCourseGen writes the bodies.
// Both stages return immediately — poll getCourseGenJob for status/progress.
export const startCourseGen       = (data)  => post('/api/outreach/courses/generate', data);
export const listCourseGenJobs    = ()      => get('/api/outreach/courses/generate');
export const getCourseGenJob      = (jobId) => get(`/api/outreach/courses/generate/${jobId}`);
export const saveCourseGenOutline = (jobId, outline) =>
  patch(`/api/outreach/courses/generate/${jobId}`, { outline });
export const runCourseGen         = (jobId) => post(`/api/outreach/courses/generate/${jobId}/run`, {});
export const cancelCourseGen      = (jobId) => del(`/api/outreach/courses/generate/${jobId}`);

// The only course endpoint that returns a section's `contentJson`; the tree
// endpoints omit bodies to keep the whole-course payload small.
export const getCourseSection     = (sectionId)           => get(`/api/outreach/courses/sections/${sectionId}`);
export const createCourseSection  = (courseId, data)      => post(`/api/outreach/courses/${courseId}/sections`, data);
export const updateCourseSection  = (sectionId, data)     => patch(`/api/outreach/courses/sections/${sectionId}`, data);
export const deleteCourseSection  = (sectionId)           => del(`/api/outreach/courses/sections/${sectionId}`);
// Section history — same shape as the blog's revision endpoints above, so both
// feed the same RevisionHistoryDrawer.
export const listCourseSectionRevisions   = (sectionId)               => get(`/api/outreach/courses/sections/${sectionId}/revisions`);
export const rollbackCourseSectionRevision = (sectionId, revId)       => post(`/api/outreach/courses/sections/${sectionId}/revisions/${revId}/rollback`, {});
export const renameCourseSectionRevision   = (sectionId, revId, name) => patch(`/api/outreach/courses/sections/${sectionId}/revisions/${revId}`, { name });
export const createCourseModule = (courseId, data)  => post(`/api/outreach/courses/${courseId}/modules`, data);
export const updateCourseModule = (moduleId, data)  => patch(`/api/outreach/courses/modules/${moduleId}`, data);
export const deleteCourseModule = (moduleId)        => del(`/api/outreach/courses/modules/${moduleId}`);
// tree: [{ moduleId, sectionIds: [] }, …] — must list EVERY module and section
// of the course exactly once, or the server 400s rather than half-applying.
export const saveCourseStructure = (courseId, tree) => put(`/api/outreach/courses/${courseId}/structure`, { tree });

export const listCourseQuestions   = (sectionId)            => get(`/api/outreach/courses/sections/${sectionId}/questions`);
// `scope` bounds which family of questions the whole-set replace may delete:
// 'quiz' = untimed only, 'popup' = in-video only, 'all' = both. The quiz editor
// only ever loads untimed questions, so without a scope its save would delete
// the section's in-video pop-ups as collateral.
export const replaceCourseQuestions = (sectionId, questions, scope = 'all') =>
  put(`/api/outreach/courses/sections/${sectionId}/questions`, { questions, scope });
export const saveCourseQuestion    = (sectionId, question)  => post(`/api/outreach/courses/sections/${sectionId}/questions`, question);
export const deleteCourseQuestion  = (sectionId, questionId) => del(`/api/outreach/courses/sections/${sectionId}/questions/${questionId}`);

// ── Course slides (SLIDES sections) ──────────────────────────
// Whether the bot account still holds drive.readonly. Only the Google Slides
// *link* import needs it; .pdf and .pptx work without.
export const getSlideCapabilities  = ()            => get('/api/outreach/courses/slide-capabilities');
export const listCourseSlides      = (sectionId)   => get(`/api/outreach/courses/sections/${sectionId}/slides`);
export const saveCourseSlideMeta   = (sectionId, slides) =>
  put(`/api/outreach/courses/sections/${sectionId}/slides`, { slides });

/**
 * No ids → clear the whole deck. With ids → delete only those, which is how a
 * re-import removes the old pages once the new ones are safely stored.
 *
 * `del` above cannot carry a body, and adding one to it would change every
 * existing caller — so this speaks to the same fetch/auth contract directly.
 */
export async function clearCourseDeck(sectionId, ids) {
  const response = await fetch(
    `${BASE_URL}/api/outreach/courses/sections/${sectionId}/slides`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...authHeaders() },
      body: JSON.stringify(ids?.length ? { ids } : {}),
    }
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, body.error ?? 'Could not remove those slides');
  }
  return response.json().catch(() => ({ ok: true }));
}

export const clearCourseAudio = (sectionId) => del(`/api/outreach/courses/sections/${sectionId}/audio`);
export const recordCourseSlideProgress = (sectionId, index) =>
  post(`/api/outreach/courses/sections/${sectionId}/slide-progress`, { index });

/** Multipart narration upload with progress. Mirrors uploadVaultFile. */
export function uploadCourseAudio(sectionId, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE_URL}/api/outreach/courses/sections/${sectionId}/audio`);
    xhr.withCredentials = true;
    Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let body = null; try { body = JSON.parse(xhr.responseText); } catch {}
      if (xhr.status >= 200 && xhr.status < 300) resolve(body);
      else reject(new Error(body?.error || `Audio upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Network error during audio upload'));
    const fd = new FormData();
    fd.append('audio', file);
    xhr.send(fd);   // no Content-Type header — FormData sets the multipart boundary
  });
}

// Learner-side. The server clamps `positionSec` against wall-clock × max rate,
// so a fabricated jump is rejected there — these are the UX half of the lock.
export const recordCourseVideoProgress = (sectionId, positionSec) =>
  post(`/api/outreach/courses/sections/${sectionId}/video-progress`, { positionSec });
export const answerCoursePopup = (sectionId, questionId, answerIds) =>
  post(`/api/outreach/courses/sections/${sectionId}/popup-answer`, { questionId, answerIds });

// The gated learner payload. Locked sections arrive WITHOUT `contentJson` /
// `videoConfig` — the player trusts that omission rather than re-deriving the gate.
// `preview` is honoured server-side for the author/admin only: every section
// unlocked, and no enrollment created.
export const getLearnerCourse = (slug, { preview = false } = {}) =>
  get(`/api/outreach/courses/${slug}/learn${preview ? '?preview=1' : ''}`);
// Completion responses carry the reward envelope (xpDelta / progressMilestones /
// achievementUnlocks), so `handleResponse` fires RewardFlux and the quest toasts
// with no extra wiring on the page.
export const completeCourseSection = (sectionId) =>
  post(`/api/outreach/courses/sections/${sectionId}/complete`, {});
export const submitCourseQuiz = (sectionId, responses) =>
  post(`/api/outreach/courses/sections/${sectionId}/quiz/attempts`, { responses });
// A LIT_REVIEW or ASSIGNMENT submission. A File goes up as multipart so the
// backend can extract its text; a pasted answer goes up as JSON. Never base64 —
// the backend's express.json() limit is 100 kb.
// These carry the reward envelope like every other completion, so
// `handleResponse` fires RewardFlux and the quest toasts with no extra wiring.
export const submitWork = (sectionId, { text, file } = {}) => {
  if (file) {
    const form = new FormData();
    form.append('file', file);
    return post(`/api/outreach/courses/sections/${sectionId}/work`, form);
  }
  return post(`/api/outreach/courses/sections/${sectionId}/work`, { text });
};
export const listWorkSubmissions = (sectionId) =>
  get(`/api/outreach/courses/sections/${sectionId}/work`);

// DEPRECATED shims so LitReviewSection keeps working until it moves to
// submitWork/listWorkSubmissions. The /lit-review routes are gone; only these
// names survive. Delete both once no component imports them.
export const submitLitReview = (sectionId, text) => submitWork(sectionId, { text });
export const listLitSubmissions = (sectionId) => listWorkSubmissions(sectionId);

export const uploadAssignmentHandout = (sectionId, file) => {
  const form = new FormData();
  form.append('file', file);
  return post(`/api/outreach/courses/sections/${sectionId}/handout`, form);
};
export const deleteAssignmentHandout = (sectionId) =>
  del(`/api/outreach/courses/sections/${sectionId}/handout`);

// ── Safety-training registry and certificates ────────────────
// NOTE: unrelated to `ensureTrainingProject` below, which is the walkthrough
// sandbox project. Same word, different feature.
export const listTrainings   = ()          => get('/api/outreach/courses/trainings');
export const createTraining  = (body)      => post('/api/outreach/courses/trainings', body);
export const updateTraining  = (id, body)  => patch(`/api/outreach/courses/trainings/${id}`, body);
export const myTrainingStatus = ()         => get('/api/outreach/courses/trainings/my-status');

export const uploadTrainingExample = (trainingId, file) => {
  const form = new FormData();
  form.append('file', file);
  return post(`/api/outreach/courses/trainings/${trainingId}/example`, form);
};
export const deleteTrainingExample = (trainingId) =>
  del(`/api/outreach/courses/trainings/${trainingId}/example`);

// Carries the reward envelope like every other completion, so `handleResponse`
// fires RewardFlux and the quest toasts with no extra wiring on the page.
export const submitCertificate = (sectionId, { file, completedOn }) => {
  const form = new FormData();
  form.append('file', file);
  form.append('completedOn', completedOn);
  return post(`/api/outreach/courses/sections/${sectionId}/certificate`, form);
};
export const listMyCertificates = (sectionId) =>
  get(`/api/outreach/courses/sections/${sectionId}/certificates`);

export const listPendingCertificates = () =>
  get('/api/outreach/courses/certificates/pending');
export const reviewCertificate = (certificateId, body) =>
  post(`/api/outreach/courses/certificates/${certificateId}/review`, body);

// These two build URLs rather than fetching, because they are used as <object>
// and <a href> targets, which cannot send the Bearer header — and Brave/Safari
// block the cross-site session cookie. So the token rides along as `?token=`,
// like conversationFileUrl. Never build these URLs by hand in a component.
const withTokenParam = (url) => {
  const token = getStoredToken();
  return token ? `${url}?token=${encodeURIComponent(token)}` : url;
};
export const certificateFileUrl = (certificateId) =>
  withTokenParam(`${BASE_URL}/api/outreach/courses/certificates/${encodeURIComponent(certificateId)}/file`);
export const trainingExampleUrl = (trainingId) =>
  withTokenParam(`${BASE_URL}/api/outreach/courses/trainings/${encodeURIComponent(trainingId)}/example-file`);

export const assignCourse = (courseId, memberIds, dueDate = null) =>
  post(`/api/outreach/courses/${courseId}/assign`, { memberIds, dueDate });

// Admin reporting (author-or-admin for the first two; admin-or-self for the third).
export const getCourseProgress     = (courseId) => get(`/api/outreach/courses/${courseId}/progress`);
export const getCourseQuizAnalysis = (courseId) => get(`/api/outreach/courses/${courseId}/quiz-analysis`);
export const getMemberCourseProgress = (memberId) => get(`/api/outreach/courses/progress/member/${memberId}`);

// ws(s):// base for the course-section Hocuspocus namespace (backend/src/collab/courseCollab.ts).
// The Yjs document name is a CourseSection.id.
export function getCourseCollabWsUrl() {
  const origin = BASE_URL || window.location.origin;
  return `${origin.replace(/^http/, 'ws')}/collab/course`;
}

// ── Lab schedule (workspaces) ────────────────────────────────

export const listWorkspaces = (params = {}) => {
  const q = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== '')
  ).toString();
  return get(`/api/workspaces${q ? `?${q}` : ''}`);
};
export const createWorkspace          = (data)                    => post('/api/workspaces', data);
export const updateWorkspace          = (id, data)                => patch(`/api/workspaces/${id}`, data);
export const archiveWorkspace         = (id)                      => del(`/api/workspaces/${id}`);
export const setWorkspaceProjects     = (id, projectIds)          => put(`/api/workspaces/${id}/projects`, { projectIds });
export const setWorkspaceRequirements = (id, trainingIds, courseIds) => put(`/api/workspaces/${id}/requirements`, { trainingIds, courseIds });
export const getWorkspaceWeek         = (id, start)               => get(`/api/workspaces/${id}/week?start=${encodeURIComponent(start)}`);
export const applyLabRect             = (id, body)                => post(`/api/workspaces/${id}/shifts/apply`, body);
export const updateLabShift           = (shiftId, data)           => patch(`/api/workspaces/shifts/${shiftId}`, data);
export const deleteLabShift           = (shiftId)                 => del(`/api/workspaces/shifts/${shiftId}`);
export const listLabBuddyRequests     = (projectId)               =>
  get(`/api/workspaces/buddy-requests${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`);

// ── Lab check-in/out (LabVisit) ──────────────────────────────
export const getMyLabVisits = () => get('/api/lab-visits/me');
export function getLabPresence({ projectId, workspaceId } = {}) {
  const q = new URLSearchParams();
  if (projectId) q.set('projectId', projectId);
  if (workspaceId) q.set('workspaceId', workspaceId);
  const s = q.toString();
  return get(`/api/lab-visits/present${s ? `?${s}` : ''}`);
}
export const labCheckIn  = (workspaceId)   => post('/api/lab-visits/check-in', { workspaceId });
export const labCheckOut = (at, wrapUp = {}) => post('/api/lab-visits/check-out', { ...(at ? { at } : {}), ...wrapUp });
export const labConfirm  = (at)            => post('/api/lab-visits/confirm', at ? { at } : {});
export const labAllocate = (visitId, taskId) => post(`/api/lab-visits/${encodeURIComponent(visitId)}/allocate`, { taskId });
export function getLabTimeSummary({ memberId, from, to } = {}) {
  const q = new URLSearchParams();
  if (memberId) q.set('memberId', memberId);
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  return get(`/api/lab-visits/time-summary${q.size ? `?${q}` : ''}`);
}
export function getProjectTimeInsights(projectId, { from, to, rollup } = {}) {
  const q = new URLSearchParams();
  if (rollup) q.set('rollup', '1');
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  const s = q.toString();
  return get(`/api/projects/${encodeURIComponent(projectId)}/time-insights${s ? `?${s}` : ''}`);
}

// ── Meeting scheduler (when2meet availability polls) ─────────

export const listMeetingPolls = (params = {}) => {
  const q = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== '')
  ).toString();
  return get(`/api/meeting-polls${q ? `?${q}` : ''}`);
};
export const getMeetingPoll        = (id)        => get(`/api/meeting-polls/${id}`);
export const getMeetingPollByToken = (token)     => get(`/api/meeting-polls/token/${token}`);
export const createMeetingPoll     = (data)      => post('/api/meeting-polls', data);
export const updateMeetingPoll     = (id, data)  => patch(`/api/meeting-polls/${id}`, data);
export const deleteMeetingPoll     = (id)        => del(`/api/meeting-polls/${id}`);
export const submitAvailability    = (id, slots) => put(`/api/meeting-polls/${id}/response`, { slots });
export const getMeetingPollResponses = (id)      => get(`/api/meeting-polls/${id}/responses`);
// Availability suggested from the caller's own answers to past polls. Requires
// auth (the habits are personal), so guests never call it.
export const getAvailabilitySuggestion = (id)    => get(`/api/meeting-polls/${id}/suggestion`);
// Slots that clash with the caller's connected calendar. Computed server-side
// so the feed URL never reaches the browser; requires auth, so guests never
// call it.
export const getPollIcsConflicts = (id, includeAllDay = false) =>
  get(`/api/meeting-polls/${id}/ics-conflicts?includeAllDay=${includeAllDay}`);
export const remindMeetingPoll     = (id)        => post(`/api/meeting-polls/${id}/remind`, {});
export const finalizeMeetingPoll   = (id, start, end) => post(`/api/meeting-polls/${id}/finalize`, { start, end });

// Public (guest shareable link) — auth optional; a logged-in member passes
// their Bearer token in the body so cookie-blocked browsers still identify them.
export const getPublicPoll = (token) => get(`/api/public/polls/${token}`);
export const submitPublicAvailability = (token, { guestName, slots, authToken }) =>
  put(`/api/public/polls/${token}/response`, { guestName, slots, authToken });

// Download the finalized meeting's .ics via authenticated fetch → blob, so it
// works under both cookie and Bearer auth (a plain <a href> omits the header).
export async function downloadMeetingPollIcs(id, filename = 'meeting.ics') {
  const response = await fetch(`${BASE_URL}/api/meeting-polls/${id}/ics`, {
    credentials: 'include',
    headers: { ...authHeaders() },
  });
  if (!response.ok) throw new ApiError(response.status, 'Failed to download calendar file');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const deleteEvent = (id) => del(`/api/events/${id}`);

export const previewEventImport = (url)            => post('/api/events/import/preview', { url });
export const importEvents       = (events, source) => post('/api/events/import', { events, source });

// Build a "Add to Google Calendar" template URL entirely client-side (no backend).
export function googleCalendarUrl({ title, description, location, start, end }) {
  const fmt = (d) => new Date(d).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title || 'Meeting',
    dates: `${fmt(start)}/${fmt(end)}`,
    ...(description ? { details: description } : {}),
    ...(location ? { location } : {}),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// Multipart image upload → { url, width, height }. Do NOT set Content-Type:
// the browser fills in the multipart boundary from the FormData body.
export async function uploadBlogImage(file) {
  const form = new FormData();
  form.append('image', file);
  const response = await fetch(`${BASE_URL}/api/blog/upload`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', ...authHeaders() },
    body: form,
  });
  return handleResponse(response);
}

// AI alt-text suggestion. Reuses the outreach endpoint, whose handler only
// reads `imageUrl` from the body and ignores the :id path param.
export const suggestBlogAltText = (imageUrl) =>
  post('/api/outreach/submissions/blog/ai/alt-text', { imageUrl });

// Mirror of backend proxyImageSrc: rewrite legacy Drive image URLs so already-
// published posts display in the editor too. New uploads are already proxied.
const DRIVE_ID_RE = /(?:drive\.google\.com\/uc\?[^"']*?[?&]id=|drive\.google\.com\/file\/d\/|lh3\.googleusercontent\.com\/d\/)([a-zA-Z0-9_-]{10,})/;
export function proxyImageSrc(src) {
  if (!src) return src;
  const m = String(src).match(DRIVE_ID_RE);
  if (!m) return src;
  return `${BASE_URL}/api/public/blog-image/${m[1]}`;
}

// Google Drive bot account connection (Workstream A — see
// docs/superpowers/specs/2026-07-06-clubpm-drive-multirepo-design.md §3).
// Connect is a full-page redirect to /auth/google (see GoogleDriveConnectButton),
// not a fetch call, so there is no connect() helper here.
export const getGoogleDriveStatus = () => get("/auth/google/status");
export const disconnectGoogleDrive = () => del("/auth/google");

// Club-wide Google Photos albums, added by share link (backend/src/api/photoAlbums.ts).
// No Google sign-in: the share link is the access grant.
export const listPhotoAlbums = () => get("/api/photo-albums");
export const addPhotoAlbum = (url, title) => post("/api/photo-albums", { url, title });
export const removePhotoAlbum = (id) => del(`/api/photo-albums/${encodeURIComponent(id)}`);
export const getPhotoAlbumPhotos = (id, { refresh = false } = {}) =>
  get(`/api/photo-albums/${encodeURIComponent(id)}/photos${refresh ? "?refresh=1" : ""}`);
// target 'blog' → { images: [{ url, width, height, photoId }], missing, failed }
// target 'asset' → same plus `assets` (new OutreachAsset rows).
export const importAlbumPhotos = (id, body) => post(`/api/photo-albums/${encodeURIComponent(id)}/import`, body);

// Multi-repo GitHub integration (Workstream B — see
// docs/superpowers/specs/2026-07-06-clubpm-drive-multirepo-design.md §4).
// All repos linked to a project are equal (no "primary" repo); adding a repo
// appends a new ProjectRepo row rather than replacing a single project field.
export const listProjectRepos  = (projectId, { rollup } = {}) =>
  get(`/api/github/projects/${projectId}/repos${rollup ? "?rollup=1" : ""}`);
export const getProjectRollup  = (id) => get(`/api/projects/${id}/rollup`);
export const saveProjectRollup = (id, body) => put(`/api/projects/${id}/rollup`, body);
export const addProjectRepo    = (projectId, url) => post(`/api/github/projects/${projectId}/repos`, { url });
export const updateProjectRepo = (repoId, body) => patch(`/api/github/repos/${repoId}`, body);
export const removeProjectRepo = (repoId) => del(`/api/github/repos/${repoId}`);

// ── Walkthrough tours ────────────────────────────────────────
export const recordTourProgress = (sectionId, stepIndex) =>
  post(`/api/outreach/courses/sections/${sectionId}/tour-progress`, { stepIndex });

export const reportTourBreakage = (sectionId, payload) =>
  post(`/api/outreach/courses/sections/${sectionId}/tour-breakage`, payload);

export const listTourBreakages = (sectionId) =>
  get(`/api/outreach/courses/sections/${sectionId}/tour-breakages`);

export const ensureTrainingProject = () => post(`/api/training-project`, {});

// ── Document access (share dialog) ───────────────────────────
export const listDocAccess = (docType, docId) =>
  get(`/api/docs/${docType}/${docId}/access`);
export const setDocAccess = (docType, docId, memberId, level) =>
  put(`/api/docs/${docType}/${docId}/access/${memberId}`, { level });
export const removeDocAccess = (docType, docId, memberId) =>
  del(`/api/docs/${docType}/${docId}/access/${memberId}`);
export const setDocClubAccess = (docType, docId, level) =>
  put(`/api/docs/${docType}/${docId}/club-access`, { level });

// ── Slack chat archive ───────────────────────────────────────

export function getChatChannels(projectId) {
  return get(`/api/projects/${projectId}/chat/channels`);
}

export function startChatBackfill(projectId, channelId) {
  return post(`/api/projects/${projectId}/chat/backfill`, { channelId });
}

export function getChatBackfillStatus(projectId, channelId) {
  return get(`/api/projects/${projectId}/chat/backfill/${channelId}`);
}

export function getChatStorageHealth(projectId) {
  return get(`/api/projects/${projectId}/chat/storage-health`);
}

export function getSlackArchiveHealth() {
  return get("/api/slack-archive/health");
}

/** Requeue every MIRROR_FAILED attachment and start a mirror sweep. → { requeued } */
export function retryFailedSlackMirrors() {
  return post("/api/slack-archive/retry-failed", {});
}

// ── Slack portal: conversation-scoped chat (/api/chat) ───────
// Everything the chat UI needs, keyed on the Slack conversation id rather than
// a project. HTTP 409 from any write means "reconnect Slack" (plan Task 11).

const chatPath = (channelId, rest = "") => `/api/chat/conversations/${encodeURIComponent(channelId)}${rest}`;

export const listConversations = (projectId = null) => get(`/api/chat/conversations${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`);
export const getConversation = (channelId) => get(chatPath(channelId));

export function getConversationMessages(channelId, before) {
  return get(chatPath(channelId, `/messages${before ? `?before=${encodeURIComponent(before)}` : ""}`));
}
export const getConversationThread = (channelId, ts) => get(chatPath(channelId, `/thread/${encodeURIComponent(ts)}`));
export const searchConversation = (channelId, q) => get(chatPath(channelId, `/search?q=${encodeURIComponent(q)}`));
export const markConversationRead = (channelId, ts) => post(chatPath(channelId, "/read"), { ts });

export const sendChatMessage = (channelId, { text, threadTs, broadcast } = {}) =>
  post(chatPath(channelId, "/messages"), { text, threadTs, broadcast });
export const editChatMessage = (channelId, ts, text) =>
  patch(chatPath(channelId, `/messages/${encodeURIComponent(ts)}`), { text });
export const deleteChatMessage = (channelId, ts) =>
  del(chatPath(channelId, `/messages/${encodeURIComponent(ts)}`));
export const reactToChatMessage = (channelId, ts, emoji, add) =>
  post(chatPath(channelId, `/messages/${encodeURIComponent(ts)}/reactions`), { emoji, add });
export const joinConversation = (channelId) => post(chatPath(channelId, "/join"), {});
/** Constellation-side mute (D8) — silences mirrored pings; Slack's own mute has no API. */
export const muteConversation = (channelId, muted) => post(chatPath(channelId, "/mute"), { muted });

/** Open (or find) a DM / group DM with these Member ids → { channelId, kind }. */
export const openDm = (memberIds) => post("/api/chat/dms", { memberIds });
/** Import the signed-in member's DM history (idempotent server-side). */
export const importMyDms = (projectId = null) => post("/api/chat/dms/import", projectId ? { projectId } : {});
/** Admin: join + import every public channel. */
export const backfillPublicChannels = () => post("/api/slack-archive/backfill-public", {});

/** Multipart upload — `post()` is JSON-only. */
export async function uploadChatFile(channelId, file, { threadTs, comment } = {}) {
  const fd = new FormData();
  fd.append("file", file);
  if (threadTs) fd.append("threadTs", threadTs);
  if (comment) fd.append("comment", comment);
  const response = await fetch(`${BASE_URL}${chatPath(channelId, "/files")}`, {
    method: "POST",
    credentials: "include",
    headers: { ...authHeaders() },
    body: fd,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, body.error ?? "Upload failed");
  }
  return response.json();
}

/**
 * URL for an archived attachment in ANY conversation. The Bearer token rides
 * along as `?token=` because an <img> cannot send headers (Brave/Safari).
 * Never build this URL by hand in a component.
 */
export function conversationFileUrl(slackFileId) {
  const token = getStoredToken();
  const base = `${BASE_URL}/api/chat/files/${encodeURIComponent(slackFileId)}`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}

// ── Skills ────────────────────────────────────────────────────
// Weekly AI-inferred skills with linked evidence. Member calls act on the signed-in
// member only; admin calls need an admin session. See backend/src/api/skills*.ts.

export const skillsApi = {
  workspace: () => get("/api/skills/workspace"),
  setGoals: (skillIds) => put("/api/skills/me/goals", { skillIds }),
  saveOpportunity: (taskId, saved) => put(`/api/skills/me/opportunities/${encodeURIComponent(taskId)}`, { saved }),
  coverage: (projectId) => get(`/api/skills/projects/${encodeURIComponent(projectId)}/coverage`),
  setSkillTeamVisible: (skillId, showToTeam) => put(`/api/skills/me/team-visibility/${encodeURIComponent(skillId)}`, { showToTeam }),
  me: () => get("/api/skills/me"),
  member: (memberId) => get(`/api/skills/members/${encodeURIComponent(memberId)}`),
  evidence: (skillId) => get(`/api/skills/me/${encodeURIComponent(skillId)}/evidence`),
  taxonomy: () => get("/api/skills/taxonomy"),
  claim: (skillId, level, note) => put(`/api/skills/me/claims/${encodeURIComponent(skillId)}`, { level, note: note || null }),
  unclaim: (skillId) => del(`/api/skills/me/claims/${encodeURIComponent(skillId)}`),
  dispute: ({ skillId, evidenceId, reason }) => post("/api/skills/me/disputes", { skillId, evidenceId: evidenceId ?? null, reason }),
  setSkillPublic: (skillId, isPublic) => put(`/api/skills/me/visibility/skills/${encodeURIComponent(skillId)}`, { isPublic }),
  setProjectPublic: (projectId, isPublic) => put(`/api/skills/me/visibility/projects/${encodeURIComponent(projectId)}`, { isPublic }),
  setPortfolio: (body) => put("/api/skills/me/portfolio", body),
  addEvidence: (body) => post("/api/skills/me/evidence", body),
  endorse: (body) => post("/api/skills/endorse", body),
  unendorse: (memberId, skillId) => del(`/api/skills/endorse/${encodeURIComponent(memberId)}/${encodeURIComponent(skillId)}`),
};

export const skillsAdminApi = {
  catalog: () => get("/api/admin/skills/catalog"),
  setCatalogPolicy: (body) => put("/api/admin/skills/catalog/policy", body),
  consolidate: (sourceId, targetId) => post("/api/admin/skills/catalog/consolidate", { sourceId, targetId }),
  reverseCatalogChange: (id) => post(`/api/admin/skills/catalog/${encodeURIComponent(id)}/reverse`, {}),
  status: () => get("/api/admin/skills/status"),
  runs: (limit = 20) => get(`/api/admin/skills/runs?limit=${limit}`),
  startRun: (body = {}) => post("/api/admin/skills/runs", body),
  cancelRun: (id) => post(`/api/admin/skills/runs/${encodeURIComponent(id)}/cancel`, {}),
  seedTaxonomy: () => post("/api/admin/skills/taxonomy/seed", {}),
  matrix: () => get("/api/admin/skills/matrix"),
  personEvidence: (memberId, skillId) => get(`/api/admin/skills/people/${encodeURIComponent(memberId)}/evidence/${encodeURIComponent(skillId)}`),
  recompute: () => post("/api/admin/skills/recompute", {}),
  embedMissing: () => post("/api/admin/skills/embed-missing", {}),
  search: (params) => get(`/api/admin/skills/search?${new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null)).toString()}`),
  ask: (question, projectId) => post("/api/admin/skills/ask", { question, projectId: projectId || undefined }),
  person: (memberId) => get(`/api/admin/skills/people/${encodeURIComponent(memberId)}`),
  disagreements: () => get("/api/admin/skills/disagreements"),
  verify: (body) => post("/api/admin/skills/endorse", body),
  addPersonEvidence: (memberId, body) => post(`/api/admin/skills/people/${encodeURIComponent(memberId)}/evidence`, body),
  disputes: (status = "OPEN") => get(`/api/admin/skills/disputes?status=${status}`),
  resolveDispute: (id, decision, resolution) => post(`/api/admin/skills/disputes/${encodeURIComponent(id)}/resolve`, { decision, resolution: resolution || null }),
  proposals: (status = "PENDING") => get(`/api/admin/skills/proposals?status=${status}`),
  approveProposal: (id, body = {}) => post(`/api/admin/skills/proposals/${encodeURIComponent(id)}/approve`, body),
  rejectProposal: (id) => post(`/api/admin/skills/proposals/${encodeURIComponent(id)}/reject`, {}),
  mergeProposal: (id, skillId) => post(`/api/admin/skills/proposals/${encodeURIComponent(id)}/merge`, { skillId }),
  taxonomy: () => get("/api/admin/skills/taxonomy"),
  createSkill: (body) => post("/api/admin/skills/taxonomy", body),
  patchSkill: (id, body) => patch(`/api/admin/skills/taxonomy/${encodeURIComponent(id)}`, body),
  addAlias: (id, alias) => post(`/api/admin/skills/taxonomy/${encodeURIComponent(id)}/aliases`, { alias }),
  removeAlias: (aliasId) => del(`/api/admin/skills/aliases/${encodeURIComponent(aliasId)}`),
  addEdge: (id, toId, kind) => post(`/api/admin/skills/taxonomy/${encodeURIComponent(id)}/edges`, { toId, kind }),
  removeEdge: (edgeId) => del(`/api/admin/skills/edges/${encodeURIComponent(edgeId)}`),
};

/** Public, unauthenticated: no cookie, no token. Resolves null when the portfolio is not enabled. */
export async function getPublicPortfolio(slug) {
  const response = await fetch(`${BASE_URL}/api/public/portfolio/${encodeURIComponent(slug)}`, { headers: { Accept: "application/json" } });
  if (response.status === 404) return null;
  if (!response.ok) throw new ApiError(response.status, "Could not load this portfolio");
  return response.json();
}
